import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Merge, Phone, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { FicheCarte } from "@/components/FicheCarte";
import { useRefs } from "@/lib/referentiels";

const CHAMPS = [["civilite", "Civilité"], ["prenom", "Prénom"], ["nom", "Nom"], ["email", "E-mail"], ["adresse", "Adresse"], ["code_postal", "Code postal"], ["ville", "Ville"], ["type", "Type"], ["source", "Source"]];

const FusionDialog = ({ ouvert, setOuvert, principal, onFusion }) => {
  const refs = useRefs();
  const [champsDefs, setChampsDefs] = useState([]);
  useEffect(() => { api.get("/champs").then((r) => setChampsDefs(r.data.filter((d) => d.cible === "contact"))); }, []);
  const [q, setQ] = useState("");
  const [liste, setListe] = useState([]);
  const [doublon, setDoublon] = useState(null);
  const [choix, setChoix] = useState({});
  useEffect(() => { if (ouvert) api.get("/contacts", { params: { recherche: q || principal.nom } }).then((r) => setListe(r.data.filter((c) => c.id !== principal.id))); }, [q, ouvert, principal]);
  const fusionner = async () => {
    try { await api.post("/contacts/fusion", { principal_id: principal.id, doublon_id: doublon.id, choix }); toast.success("Contacts fusionnés, historique rattaché."); setOuvert(false); setDoublon(null); onFusion(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  return (
    <Dialog open={ouvert} onOpenChange={setOuvert}>
      <DialogContent className="at-popover max-w-[calc(100vw-24px)] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle>Fusionner un doublon</DialogTitle>
          <DialogDescription className="at-muted">Le doublon est fusionné dans {principal.affichage}. Téléphones et tags sont cumulés.</DialogDescription>
        </DialogHeader>
        {!doublon ? (
          <div className="space-y-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher le doublon…" className="at-input" data-testid="merge-search-input" />
            {liste.map((c) => (
              <button key={c.id} type="button" onClick={() => setDoublon(c)} className="at-soft w-full text-left p-3 min-h-[48px]" data-testid={`merge-candidate-${c.id}`}>
                <span className="font-semibold">{c.affichage}</span> <span className="text-sm at-muted">{(c.telephones || []).join(", ")} · {c.ville}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,1fr)] gap-2 at-label px-1"><span>Champ</span><span>Garder (principal)</span><span>Garder (doublon)</span></div>
            {[...CHAMPS, ...champsDefs.filter((d) => principal.champs?.[d.cle] !== undefined || doublon.champs?.[d.cle] !== undefined).map((d) => [`champs.${d.cle}`, d.libelle])].map(([k, l]) => (
              <div key={k} className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,1fr)] gap-2 items-center text-sm" data-testid={`merge-field-${k}`}>
                <span className="font-semibold">{l}</span>
                {["principal", "doublon"].map((cote) => {
                  const src = cote === "principal" ? principal : doublon;
                  const brut = k.startsWith("champs.") ? src.champs?.[k.slice(7)] : src[k];
                  const v = k === "type" ? refs?.types_contact?.[brut] : brut;
                  return (
                    <label key={cote} className="at-soft p-2 flex items-center gap-2 min-w-0 min-h-[44px] cursor-pointer">
                      <input type="radio" name={k} checked={(choix[k] || "principal") === cote} onChange={() => setChoix({ ...choix, [k]: cote })} data-testid={`merge-choice-${k}-${cote}`} />
                      <span className="min-w-0 [overflow-wrap:anywhere]">{v === undefined || v === "" || v === null ? "—" : String(v)}</span>
                    </label>
                  );
                })}
              </div>
            ))}
            <button type="button" onClick={fusionner} className="at-btn at-btn-primary w-full mt-2" data-testid="merge-confirm-button"><Merge size={16} />Fusionner</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function ContactPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [erreur, setErreur] = useState("");
  const [fusion, setFusion] = useState(false);
  const charger = useCallback(() => api.get(`/contacts/${id}`).then((r) => setC(r.data)).catch((e) => setErreur(messageErreur(e))), [id]);
  useEffect(() => { charger(); }, [charger]);
  if (erreur) return <div className="at-card p-8" data-testid="contact-error">{erreur}</div>;
  if (!c) return null;
  return (
    <div className="max-w-4xl" data-testid="page-contact">
      <Link to="/fiches" className="inline-flex items-center gap-2 min-h-[44px] font-semibold text-sm"><ArrowLeft size={16} />Fiches</Link>
      <div className="at-card p-5 md:p-7 mb-4">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="contact-name">{c.civilite} {c.affichage}</h1>
        {c.est_client && <span className="inline-block mt-2 text-xs font-bold at-pill px-2.5 py-1" style={{ background: "var(--success)", color: "var(--accent-ink)" }} data-testid="contact-client-badge">Client depuis le {new Date(c.client_depuis).toLocaleDateString("fr-FR")}</span>}
        {c.organisation && <p className="at-muted">{c.organisation.nom}</p>}
        <div className="mt-3 space-y-1">
          {(c.telephones || []).map((t, i) => <a key={t} href={`tel:${t.replace(/\s/g, "")}`} data-testid={`contact-page-phone-link-${i}`} className="flex items-center gap-2 min-h-[44px] font-semibold"><Phone size={15} />{t}</a>)}
          {c.email && <a href={`mailto:${c.email}`} data-testid="contact-page-email-link" className="flex items-center gap-2 min-h-[44px] break-all"><Mail size={15} />{c.email}</a>}
          <p className="text-sm at-muted">{c.adresse} {c.code_postal} {c.ville}</p>
        </div>
        {user.role !== "partenaire" && user.role !== "confirmateur" && <button type="button" onClick={() => setFusion(true)} className="at-btn at-btn-ghost mt-4" data-testid="contact-merge-button"><Merge size={16} />Fusionner un doublon</button>}
      </div>
      <h2 className="text-base md:text-lg font-bold mb-3">Fiches</h2>
      {c.fiches.length === 0 ? <p className="at-muted text-sm">Aucune fiche pour ce contact.</p> : <div className="grid md:grid-cols-2 gap-3">{c.fiches.map((f) => <FicheCarte key={f.id} fiche={f} />)}</div>}
      <FusionDialog ouvert={fusion} setOuvert={setFusion} principal={c} onFusion={charger} />
    </div>
  );
}
